import React, { useState, useEffect, useMemo } from "react";
import Swal from "sweetalert2";
import { useAuth } from "../auth/AuthContext";
import { SearchBar } from "../components/SearchBar";
import { Pagination } from "../components/Pagination";
import Select from "../components/Select";

// Mapeo de type_id a nombre
const TYPE_LABELS = {
  1: "Admin",
  2: "User",
  3: "User",
  4: "User",
  5: "User",
};

const TYPE_OPTIONS = [
  { value: "1", label: "Admin" },
  { value: "2", label: "User" },
];

function normalizeTypeId(typeId) {
  return Number(typeId) === 1 ? 1 : 2;
}

export default function Usuarios() {
  const { authFetch } = useAuth();

  const [users, setUsers] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [editingUserId, setEditingUserId] = useState(null);
  const [selectedType, setSelectedType] = useState({});
  const [updating, setUpdating] = useState(null);

  // Search
  const [search, setSearch] = useState("");

  // Sorting
  const [sortColumn, setSortColumn] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");

  // Pagination
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);

  // Load users
  useEffect(() => {
    const loadUsers = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await authFetch("/api/auth/users");

        if (!response.ok) {
          let errorMsg = "Error loading users";
          try {
            const data = await response.json();
            errorMsg = data.error || `Error ${response.status}`;
          } catch {
            errorMsg = `Error ${response.status}: ${response.statusText}`;
          }
          throw new Error(errorMsg);
        }

        const data = await response.json();
        const loadedUsers = data.users || [];
        setUsers(loadedUsers);

        const typeMap = {};

        loadedUsers.forEach((u) => {
          typeMap[u.id] = String(normalizeTypeId(u.type_id));
        });

        setSelectedType(typeMap);
      } catch (err) {
        console.error("Load users error:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, [authFetch]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setPage(1);
  };

  const handleUpdateUser = async (userId, user) => {
    const newType = Number(selectedType[userId]);

    const typeChanged = newType !== normalizeTypeId(user.type_id);

    if (!typeChanged) {
      await Swal.fire({
        icon: "info",
        title: "Info",
        text: "No changes were made",
        confirmButtonColor: "#3085d6",
      });
      return;
    }

    const changes = [];
    if (typeChanged) changes.push(`type to ${TYPE_LABELS[newType]}`);

    const confirm = await Swal.fire({
      icon: "warning",
      title: "Confirm",
      text: `Change ${changes.join(" and ")}?`,
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      confirmButtonColor: "#3085d6",
    });
    if (!confirm.isConfirmed) {
      return;
    }

    try {
      setUpdating(userId);

      if (typeChanged) {
        const response = await authFetch(`/api/auth/users/${userId}/type`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ type_id: newType }),
        });

        if (!response.ok) {
          let msg = "Error updating type";
          try {
            const data = await response.json();
            msg = data.error || msg;
          } catch {}
          throw new Error(msg);
        }
      }

      setUsers((prev) =>
        prev.map((u) =>
          u.id === userId
            ? {
                ...u,
                ...(typeChanged && { type_id: newType }),
              }
            : u,
        ),
      );

      setEditingUserId(null);
      await Swal.fire({
        icon: "success",
        title: "Success",
        text: "User updated successfully",
        confirmButtonColor: "#3085d6",
      });
    } catch (err) {
      console.error("Update error:", err);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message,
        confirmButtonColor: "#3085d6",
      });
    } finally {
      setUpdating(null);
    }
  };

  const filtered = useMemo(() => {
    let result = users;

    if (search.trim()) {
      const term = search.toLowerCase();
      result = result.filter(
        (u) =>
          (u.full_name || "").toLowerCase().includes(term) ||
          (u.email || "").toLowerCase().includes(term) ||
          (u.organization_name || "").toLowerCase().includes(term) ||
          (u.country || "").toLowerCase().includes(term),
      );
    }

    if (sortColumn) {
      result = [...result].sort((a, b) => {
        const av = a[sortColumn]?.toString().toLowerCase() ?? "";
        const bv = b[sortColumn]?.toString().toLowerCase() ?? "";
        const dir = sortDirection === "asc" ? 1 : -1;

        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return 0;
      });
    }

    return result;
  }, [users, search, sortColumn, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const pageData = filtered.slice((page - 1) * perPage, page * perPage);

  if (loading) {
    return (
      <div className="w-full p-6 flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading users...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="text-red-800 font-semibold mb-2">⚠️ Error</h3>
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-slate-50 shadow-sm w-full h-screen flex flex-col font-montserrat">
      <div className="p-4 md:p-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">User Management</h1>
        <p className="text-gray-600">
          Modify the user type for {users.length} registered user(s)
        </p>
      </div>

      <div className="px-4 md:px-6 pb-4">
        <div className="bg-white rounded-lg shadow-sm p-4">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Search by name, email, organization or country..."
          />
        </div>
      </div>

      <div className="px-4 md:px-6 pb-4 flex-1 flex flex-col min-h-0">
        <div className="bg-white rounded-lg shadow-sm flex flex-col flex-1 overflow-hidden">
          <div className="overflow-x-auto overflow-y-auto flex-1">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                <tr>
                  <th
                    onClick={() => handleSort("full_name")}
                    className="px-6 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                  >
                    Name {sortColumn === "full_name" && (sortDirection === "asc" ? "↑" : "↓")}
                  </th>
                  <th
                    onClick={() => handleSort("email")}
                    className="px-6 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                  >
                    Email {sortColumn === "email" && (sortDirection === "asc" ? "↑" : "↓")}
                  </th>
                  <th
                    onClick={() => handleSort("organization_name")}
                    className="px-6 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                  >
                    Organization {sortColumn === "organization_name" && (sortDirection === "asc" ? "↑" : "↓")}
                  </th>
                  <th
                    onClick={() => handleSort("country")}
                    className="px-6 py-3 w-40 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                  >
                    Country {sortColumn === "country" && (sortDirection === "asc" ? "↑" : "↓")}
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200">
                {pageData.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 transition">
                    <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                      {user.full_name || "N/A"}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 break-all">
                      {user.email}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {user.organization_name || "N/A"}
                    </td>
                    <td className="px-6 py-4 w-40 max-w-[160px] text-sm text-gray-600 truncate" title={user.country || "N/A"}>
                      {user.country || "N/A"}
                    </td>
                    <td className="px-6 py-4 min-w-[180px]">
                      {editingUserId === user.id ? (
                        <div className="max-w-[170px]">
                          <Select
                            value={selectedType[user.id] || String(normalizeTypeId(user.type_id))}
                            onChange={(value) =>
                              setSelectedType((prev) => ({
                                ...prev,
                                [user.id]: value,
                              }))
                            }
                            options={TYPE_OPTIONS}
                            placeholder="Select type"
                            disabled={updating === user.id}
                            className="text-sm"
                          />
                        </div>
                      ) : (
                        <span className="inline-flex px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">
                          {TYPE_LABELS[user.type_id]}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm space-x-2 whitespace-nowrap">
                      {editingUserId === user.id ? (
                        <>
                          <button
                            onClick={() => handleUpdateUser(user.id, user)}
                            disabled={updating === user.id}
                            className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                          >
                            {updating === user.id ? "Saving..." : "Save"}
                          </button>
                          <button
                            onClick={() => setEditingUserId(null)}
                            disabled={updating === user.id}
                            className="px-3 py-1 bg-gray-400 text-white rounded hover:bg-gray-500 disabled:opacity-50"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => {
                            setEditingUserId(user.id);
                            setSelectedType((prev) => ({
                              ...prev,
                              [user.id]: String(normalizeTypeId(user.type_id)),
                            }));
                          }}
                          className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {pageData.length === 0 && (
              <div className="px-6 py-12 text-center text-gray-500">
                <p className="text-lg">No users to display</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="px-4 md:px-6 pb-4">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </div>
        </div>
      )}

      <div className="px-4 md:px-6 pb-4">
        <div className="text-sm text-gray-600 text-center bg-blue-50 rounded-lg p-4">
          <p>
            Showing <strong>{pageData.length}</strong> of{" "}
            <strong>{filtered.length}</strong> user(s)
            {search.trim() && ` (filtered from ${users.length} total)`}
          </p>
        </div>
      </div>
    </div>
  );
}